/* LT-AO-01 — Custom Dictionary admin at /settings/dictionary.
 *
 * Layer-1 spell check (browser-native via spellcheck="true") is already
 * enabled across the app. Layer-2 is data-only here: this admin page lets
 * users add/curate construction-specific terms. The actual JS-based
 * augmentation that suppresses red squigglies for these terms is a future
 * enhancement (browsers don't expose the spell-check dictionary API directly).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, BookOpen, Pencil, Trash2, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton, EmptyState } from "../components/UI";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

const cls = (...a) => a.filter(Boolean).join(" ");

const CATEGORIES = [
  ["trade_abbrev",     "Trade abbreviations"],
  ["construction_term","Construction terms"],
  ["brand",            "Brand names"],
  ["regs",             "Regulations"],
  ["lux_specific",     "Lux-specific"],
  ["other",            "Other"],
];

function categoryLabel(k) {
  return CATEGORIES.find(([key]) => key === k)?.[1] || k;
}

export default function Dictionary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, term, category }

  const load = () => {
    setLoading(true);
    api.get("/dictionary", { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load dictionary"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (it) => !needle || (it.term || "").toLowerCase().includes(needle);
    const map = {};
    for (const it of items) {
      if (!matches(it)) continue;
      (map[it.category] ||= []).push(it);
    }
    for (const k in map) map[k].sort((a, b) => (a.term || "").localeCompare(b.term || ""));
    return CATEGORIES
      .map(([k, l]) => ({ key: k, label: l, items: map[k] || [] }))
      .filter((g) => g.items.length > 0);
  }, [items, q]);

  const totalFiltered = grouped.reduce((s, g) => s + g.items.length, 0);

  const onSave = async (entry) => {
    try {
      if (entry.id) {
        await api.put(`/dictionary/${entry.id}`, { term: entry.term, category: entry.category });
        toast.success("Term updated");
      } else {
        await api.post("/dictionary", { term: entry.term, category: entry.category });
        toast.success("Term added");
      }
      setEditing(null);
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onArchive = async (id) => {
    if (!window.confirm("Archive this term?")) return;
    try {
      await api.delete(`/dictionary/${id}`);
      toast.success("Term archived");
      load();
    } catch {
      toast.error("Archive failed");
    }
  };

  return (
    <div className="app-container py-8">
      <button
        onClick={() => navigate("/settings")}
        className="inline-flex items-center gap-1 text-ink-3 hover:text-ink mb-3"
        style={{ fontSize: 12 }}
      >
        <ArrowLeft size={12} /> Back to Settings
      </button>
      <PageHeader
        title="Custom Dictionary"
        subtitle="Construction-specific terms to teach the spell-checker. Used by Layer-2 spell-check (data layer; in-textarea augmentation is a future enhancement)."
        actions={<PrimaryButton icon={Plus} onClick={() => setEditing({ term: "", category: "trade_abbrev" })} data-testid="dictionary-add">Add term</PrimaryButton>}
      />

      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search terms…" spellCheck="false"
            data-testid="dictionary-search"
            className="w-full h-9 pl-9 pr-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 13 }}
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>
            {totalFiltered} term{totalFiltered === 1 ? "" : "s"} · {grouped.length} categor{grouped.length === 1 ? "y" : "ies"}
          </span>
          <label className="inline-flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="dictionary-show-archived" />
            Show archived
          </label>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title="No terms yet"
            hint="Seed will run on next backend startup, or add one manually."
            action={<PrimaryButton icon={Plus} onClick={() => setEditing({ term: "", category: "trade_abbrev" })}>Add term</PrimaryButton>}
          />
        ) : grouped.length === 0 ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12.5 }}>No matching terms.</div>
        ) : (
          <div className="divide-y divide-line">
            {grouped.map((g) => (
              <div key={g.key}>
                <div
                  className="px-4 py-2.5"
                  style={{ background: "#F5F3EE" }}
                  data-testid={`dictionary-group-${g.key}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono uppercase text-ink-2"
                      style={{ fontSize: 10.5, letterSpacing: "0.06em", fontWeight: 700 }}>
                      {g.label}
                    </span>
                    <span className="text-ink-3" style={{ fontSize: 11 }}>({g.items.length})</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 p-4">
                  {g.items.map((t) => (
                    <div key={t.id}
                      data-testid={`dictionary-row-${t.id}`}
                      className={cls(
                        "inline-flex items-center gap-1.5 pl-3 pr-1.5 h-7 rounded-md border bg-surface group",
                        t.is_active === false ? "border-line-soft text-ink-3 opacity-50" : "border-line text-ink-2 hover:border-accent",
                      )}
                      style={{ fontSize: 12 }}
                    >
                      <span className="font-mono">{t.term}</span>
                      {t.is_active !== false && (
                        <>
                          <button
                            onClick={() => setEditing({ id: t.id, term: t.term, category: t.category })}
                            data-testid={`dictionary-edit-${t.id}`}
                            className="text-ink-3 hover:text-accent p-0.5 opacity-0 group-hover:opacity-100"
                            title="Edit"
                          ><Pencil size={11} /></button>
                          <button
                            onClick={() => onArchive(t.id)}
                            data-testid={`dictionary-archive-${t.id}`}
                            className="text-ink-3 hover:text-note-red p-0.5 opacity-0 group-hover:opacity-100"
                            title="Archive"
                          ><Trash2 size={11} /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <EditDialog
          entry={editing}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function EditDialog({ entry, onClose, onSave }) {
  const [term, setTerm] = useState(entry.term || "");
  const [category, setCategory] = useState(entry.category || "trade_abbrev");
  const submit = () => {
    if (!term.trim()) {
      toast.error("Term is required");
      return;
    }
    onSave({ id: entry.id, term: term.trim(), category });
  };
  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-50" onClick={onClose} />
      <div
        className="fixed inset-0 z-50 flex items-start justify-center pt-32 pointer-events-none"
        data-testid="dictionary-edit-dialog"
      >
        <div className="bg-surface border border-line rounded-lg shadow-card w-[460px] max-w-[90vw] pointer-events-auto overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-line">
            <h2 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              {entry.id ? "Edit term" : "Add term"}
            </h2>
            <button onClick={onClose} className="text-ink-3 hover:text-ink"><X size={16} /></button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="col-head text-ink-3 block mb-1">Term</label>
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                spellCheck="false"
                autoFocus
                data-testid="dictionary-edit-term"
                className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                style={{ fontSize: 13 }}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              />
            </div>
            <div>
              <label className="col-head text-ink-3 block mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="dictionary-edit-category"
                className="w-full h-10 px-3 rounded-md border border-line bg-surface text-ink"
                style={{ fontSize: 13 }}
              >
                {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line bg-surface-2/30">
            <button onClick={onClose}
              className="h-8 px-4 rounded-md text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12 }}
            >Cancel</button>
            <button onClick={submit}
              data-testid="dictionary-edit-submit"
              className="h-8 px-4 rounded-md bg-accent text-white hover:bg-accent-deep"
              style={{ fontSize: 12, fontWeight: 500 }}
            >Save</button>
          </div>
        </div>
      </div>
    </>
  );
}
